/**
 * ScreenAutomationService.js
 *
 * AI-controlled screen automation for event intake workflows.
 * Receives a sequence of actions from Buddha and executes them in order,
 * navigating screens, dispatching actions, and reporting progress.
 *
 * The user watches as the AI "drives" the browser through multiple screens.
 */

class ScreenAutomationService {
  constructor() {
    this.actionQueue = [];
    this.isRunning = false;
    this.isPaused = false;
    this.currentActionIndex = 0;
    this.onProgress = null;
    this.onComplete = null;
    this.onError = null;
    this.sessionId = null;
    this.createdRecords = []; // Track for potential rollback

    // Harness API URL - same as UnifiedAIBrainService uses
    this.harnessUrl = import.meta.env.VITE_AI_SERVER_URL || 'https://ai.opendoorvalet.com';
  }

  /**
   * Execute a sequence of automation actions
   * @param {Array} actions - Array of action objects from Buddha
   * @param {Object} callbacks - { onProgress, onComplete, onError }
   */
  async executeSequence(actions, callbacks = {}) {
    if (this.isRunning) {
      console.warn('[ScreenAutomation] Already running a sequence');
      return { success: false, error: 'Already running' };
    }

    this.isRunning = true;
    this.isPaused = false;
    this.actionQueue = actions;
    this.currentActionIndex = 0;
    this.onProgress = callbacks.onProgress;
    this.onComplete = callbacks.onComplete;
    this.onError = callbacks.onError;
    this.sessionId = `automation-${Date.now()}`;
    this.createdRecords = [];

    // Store sequence in sessionStorage for resume capability
    sessionStorage.setItem('__ODV_AUTOMATION_SEQUENCE__', JSON.stringify({
      sessionId: this.sessionId,
      actions: actions,
      currentIndex: 0,
      startedAt: new Date().toISOString()
    }));

    // Show automation overlay
    this.showOverlay();

    try {
      for (let i = 0; i < actions.length; i++) {
        if (this.isPaused) {
          // Save state for resume
          sessionStorage.setItem('__ODV_AUTOMATION_SEQUENCE__', JSON.stringify({
            sessionId: this.sessionId,
            actions: actions,
            currentIndex: i,
            pausedAt: new Date().toISOString()
          }));
          break;
        }

        this.currentActionIndex = i;
        const action = actions[i];

        // Report progress
        this.onProgress?.({
          step: i + 1,
          total: actions.length,
          action: action,
          message: action.message || `Step ${i + 1}/${actions.length}`
        });

        await this.executeAction(action);

        // Wait for screen to be ready after navigation
        if (action.type === 'navigate') {
          await this.waitForScreenReady(action.waitFor);
        }
      }

      // Complete
      this.hideOverlay();
      sessionStorage.removeItem('__ODV_AUTOMATION_SEQUENCE__');
      this.isRunning = false;

      this.onComplete?.({
        success: true,
        actionsExecuted: this.currentActionIndex + 1,
        createdRecords: this.createdRecords
      });

      return { success: true, createdRecords: this.createdRecords };

    } catch (error) {
      console.error('[ScreenAutomation] Error:', error);
      this.hideOverlay();
      this.isRunning = false;

      this.onError?.({
        error: error.message,
        failedAt: this.currentActionIndex,
        action: actions[this.currentActionIndex],
        canRetry: true
      });

      return { success: false, error: error.message };
    }
  }

  /**
   * Execute a single action
   */
  async executeAction(action) {
    console.log('[ScreenAutomation] Executing:', action.type, action);

    switch (action.type) {
      case 'navigate':
        await this.handleNavigate(action);
        break;

      case 'set_context':
        this.handleSetContext(action);
        break;

      case 'dispatch_action':
        await this.handleDispatchAction(action);
        break;

      case 'wait':
        await this.handleWait(action);
        break;

      case 'create_record':
        await this.handleCreateRecord(action);
        break;

      case 'update_record':
        await this.handleUpdateRecord(action);
        break;

      case 'delete_record':
        await this.handleDeleteRecord(action);
        break;

      case 'update_overlay':
        this.updateOverlayMessage(action.message);
        break;

      case 'complete':
        // Final action - just update message
        this.updateOverlayMessage(action.message || '🎉 Complete!');
        break;

      default:
        console.warn('[ScreenAutomation] Unknown action type:', action.type);
    }
  }

  /**
   * Navigate to a new route
   */
  async handleNavigate(action) {
    return new Promise((resolve) => {
      // Use React Router if available, otherwise direct navigation
      if (window.__ODV_NAVIGATE__) {
        window.__ODV_NAVIGATE__(action.route);
        // Give React Router time to update
        setTimeout(resolve, 300);
      } else {
        // Track current location to detect change
        const currentPath = window.location.pathname;
        window.location.href = action.route;

        // Wait for navigation (handled by waitForScreenReady)
        resolve();
      }
    });
  }

  /**
   * Set localStorage context for target screen
   */
  handleSetContext(action) {
    if (action.locationId) {
      localStorage.setItem('odv-selected-location-id', action.locationId);
    }
    if (action.clientId) {
      localStorage.setItem('odv-selected-client-id', action.clientId);
    }
    if (action.weekDate) {
      localStorage.setItem('odv-selected-week', action.weekDate);
    }
    if (action.eventData) {
      localStorage.setItem('odv-selected-event', JSON.stringify(action.eventData));
    }
  }

  /**
   * Dispatch a custom event for component to handle
   */
  async handleDispatchAction(action) {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Action '${action.action}' timed out after 10s`));
      }, 10000);

      // Listen for action completion
      const completeHandler = (e) => {
        if (e.detail.actionId === action.actionId || e.detail.action === action.action) {
          clearTimeout(timeoutId);
          window.removeEventListener('ai-action-complete', completeHandler);

          if (e.detail.success) {
            if (e.detail.recordId) {
              this.createdRecords.push({
                type: action.action,
                id: e.detail.recordId,
                data: e.detail.data
              });
            }
            resolve(e.detail);
          } else {
            reject(new Error(e.detail.error || 'Action failed'));
          }
        }
      };
      window.addEventListener('ai-action-complete', completeHandler);

      // Dispatch the action
      window.dispatchEvent(new CustomEvent('ai-action', {
        detail: {
          actionId: action.actionId || `action-${Date.now()}`,
          action: action.action,
          data: action.data
        }
      }));
    });
  }

  /**
   * Wait for specified duration
   */
  async handleWait(action) {
    const duration = action.duration || 500;
    return new Promise(resolve => setTimeout(resolve, duration));
  }

  /**
   * Create a record directly (bypasses UI)
   */
  async handleCreateRecord(action) {
    // This calls the harness API directly for simpler cases
    const response = await fetch(`${this.harnessUrl}/api/automation/create-record`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        table: action.table,
        data: action.data
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(`Failed to create record: ${error.error || response.statusText}`);
    }

    const result = await response.json();
    this.createdRecords.push({
      type: action.table,
      id: result.id,
      data: result.data
    });

    return result;
  }

  /**
   * Update a record directly (bypasses UI)
   */
  async handleUpdateRecord(action) {
    const response = await fetch(`${this.harnessUrl}/api/automation/update-record`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        table: action.table,
        id: action.id,
        data: action.data
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(`Failed to update record: ${error.error || response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Delete a record (for rollback scenarios)
   */
  async handleDeleteRecord(action) {
    const response = await fetch(`${this.harnessUrl}/api/automation/delete-record`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        table: action.table,
        id: action.id
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(`Failed to delete record: ${error.error || response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Wait for screen to be ready after navigation
   */
  async waitForScreenReady(waitFor = {}) {
    const maxWait = waitFor.timeout || 5000;
    const checkInterval = 100;
    const startTime = Date.now();

    return new Promise((resolve) => {
      const check = () => {
        // Check if specific element exists
        if (waitFor.selector) {
          const element = document.querySelector(waitFor.selector);
          if (element) {
            resolve();
            return;
          }
        }

        // Check if screen context is set
        if (waitFor.contextKey && window.__ODV_SCREEN_CONTEXT__?.[waitFor.contextKey]) {
          resolve();
          return;
        }

        // Check for general page readiness
        if (!waitFor.selector && !waitFor.contextKey) {
          // Default: wait for body to have children and no loading indicators
          const loading = document.querySelector('[data-loading="true"]');
          if (!loading && document.body.children.length > 0) {
            resolve();
            return;
          }
        }

        // Timeout
        if (Date.now() - startTime > maxWait) {
          console.warn('[ScreenAutomation] Wait timeout, proceeding anyway');
          resolve();
          return;
        }

        // Keep checking
        setTimeout(check, checkInterval);
      };

      // Start checking after a short delay for navigation
      setTimeout(check, 200);
    });
  }

  /**
   * Show automation overlay
   */
  showOverlay() {
    // Remove existing overlay if any
    this.hideOverlay();

    const overlay = document.createElement('div');
    overlay.id = 'ai-automation-overlay';
    overlay.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.3);
        z-index: 9998;
        pointer-events: none;
      "></div>
      <div id="ai-automation-status" style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        border: 1px solid #aea36d;
        border-radius: 12px;
        padding: 16px 24px;
        z-index: 9999;
        color: white;
        font-family: 'Inter', sans-serif;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
        min-width: 280px;
      ">
        <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 8px;">
          <div style="
            width: 12px;
            height: 12px;
            background: #aea36d;
            border-radius: 50%;
            animation: pulse 1.5s infinite;
          "></div>
          <span style="font-weight: 600; color: #aea36d;">AI is working...</span>
        </div>
        <div id="ai-automation-message" style="
          color: #a0a0a0;
          font-size: 14px;
        ">Initializing...</div>
        <div id="ai-automation-progress" style="
          margin-top: 12px;
          height: 4px;
          background: rgba(255,255,255,0.1);
          border-radius: 2px;
          overflow: hidden;
        ">
          <div id="ai-automation-progress-bar" style="
            height: 100%;
            width: 0%;
            background: #aea36d;
            transition: width 0.3s ease;
          "></div>
        </div>
      </div>
      <style>
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      </style>
    `;

    document.body.appendChild(overlay);
  }

  /**
   * Hide automation overlay
   */
  hideOverlay() {
    const overlay = document.getElementById('ai-automation-overlay');
    if (overlay) {
      overlay.remove();
    }
  }

  /**
   * Update overlay message and progress
   */
  updateOverlayMessage(message, progress = null) {
    const messageEl = document.getElementById('ai-automation-message');
    const progressBar = document.getElementById('ai-automation-progress-bar');

    if (messageEl) {
      messageEl.textContent = message;
    }

    if (progressBar && progress !== null) {
      progressBar.style.width = `${progress}%`;
    }
  }

  /**
   * Pause the current sequence
   */
  pause() {
    this.isPaused = true;
  }

  /**
   * Resume a paused sequence
   */
  async resume() {
    const saved = sessionStorage.getItem('__ODV_AUTOMATION_SEQUENCE__');
    if (!saved) {
      console.warn('[ScreenAutomation] No saved sequence to resume');
      return;
    }

    const { actions, currentIndex } = JSON.parse(saved);
    const remainingActions = actions.slice(currentIndex);

    this.isPaused = false;
    await this.executeSequence(remainingActions, {
      onProgress: this.onProgress,
      onComplete: this.onComplete,
      onError: this.onError
    });
  }

  /**
   * Cancel the current sequence
   */
  cancel() {
    this.isPaused = true;
    this.isRunning = false;
    this.hideOverlay();
    sessionStorage.removeItem('__ODV_AUTOMATION_SEQUENCE__');
  }

  /**
   * Check if there's a pending sequence to resume
   */
  static hasPendingSequence() {
    return !!sessionStorage.getItem('__ODV_AUTOMATION_SEQUENCE__');
  }
}

// Singleton instance
const screenAutomation = new ScreenAutomationService();

// Expose for React Router integration
window.__ODV_SCREEN_AUTOMATION__ = screenAutomation;

export default screenAutomation;
export { ScreenAutomationService };
